/** @format */

import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { collection, onSnapshot, orderBy, query, where, QuerySnapshot, DocumentData, limit } from 'firebase/firestore';
import { db } from '../firebase/config';

export type MeetingLogAction = 'create' | 'start' | 'join' | 'recording_available' | string;

export interface MeetingLogEntry {
  id: string;
  meetingId: string;
  classroomId: string;
  userId: string;
  isHost?: boolean;
  action: MeetingLogAction;
  timestamp?: any; // Firestore timestamp
}

// Debounce function to limit rapid updates
const debounce = <T extends (...args: any[]) => any>(func: T, wait: number): T => {
  let timeout: NodeJS.Timeout;
  return ((...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  }) as T;
};

// Use requestAnimationFrame for batched updates
const batchUpdate = (callback: () => void) => {
  if (typeof requestAnimationFrame !== 'undefined') {
    requestAnimationFrame(callback);
  } else {
    setTimeout(callback, 0);
  }
};

// Performance monitoring utility
const measurePerformance = (name: string, callback: () => void) => {
  if (typeof performance !== 'undefined' && performance.mark) {
    const startMark = `${name}-start`;
    const endMark = `${name}-end`;
    
    performance.mark(startMark);
    callback();
    performance.mark(endMark);
    
    performance.measure(name, startMark, endMark);
    
    const measure = performance.getEntriesByName(name)[0];
    if (measure && measure.duration > 50) {
      console.warn(`Long task detected in ${name}:`, { 
        duration: Math.round(measure.duration), 
        startTime: Math.round(measure.startTime),
        name 
      });
    }
    
    // Clean up marks and measures
    performance.clearMarks(startMark);
    performance.clearMarks(endMark);
    performance.clearMeasures(name);
  } else {
    // Fallback for environments without performance API
    callback();
  }
};

export const useMeetingAttendance = (meetingId?: string) => {
  const [participantIds, setParticipantIds] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const unsubRef = useRef<(() => void) | null>(null);

  // Memoize the query to prevent unnecessary re-creation
  const meetingQuery = useMemo(() => {
    if (!meetingId) return null;
    return query(
      collection(db, 'meetingLogs'),
      where('meetingId', '==', meetingId),
      where('action', '==', 'join')
    );
  }, [meetingId]);

  // Debounced update function to prevent rapid state changes
  const debouncedUpdate = useCallback(
    debounce((snap: QuerySnapshot<DocumentData>) => {
      measurePerformance('meeting-attendance-update', () => {
        batchUpdate(() => {
          const ids = new Set<string>();
          snap.forEach((d) => {
            const data = d.data() as MeetingLogEntry;
            if (data.userId) ids.add(data.userId);
          });
          setParticipantIds(Array.from(ids));
          setLoading(false);
        });
      });
    }, 100),
    []
  );

  useEffect(() => {
    if (!meetingId || !meetingQuery) {
      setParticipantIds([]);
      setLoading(false);
      return;
    }

    // Clean up previous listener
    if (unsubRef.current) {
      unsubRef.current();
    }

    const unsub = onSnapshot(meetingQuery, debouncedUpdate, (error) => {
      console.error('Meeting attendance listener error:', error);
      setLoading(false);
    });

    unsubRef.current = unsub;

    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [meetingId, meetingQuery, debouncedUpdate]);

  return {
    participantIds,
    count: participantIds.length,
    loading,
  };
};

export const useMeetingActivity = (meetingId?: string, max: number = 20) => {
  const [logs, setLogs] = useState<MeetingLogEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const unsubRef = useRef<(() => void) | null>(null);

  // Memoize the query to prevent unnecessary re-creation
  const meetingQuery = useMemo(() => {
    if (!meetingId) return null;
    return query(
      collection(db, 'meetingLogs'),
      where('meetingId', '==', meetingId),
      orderBy('timestamp', 'desc'),
      limit(max)
    );
  }, [meetingId, max]);

  // Debounced update function to prevent rapid state changes
  const debouncedUpdate = useCallback(
    debounce((snap: QuerySnapshot<DocumentData>) => {
      measurePerformance('meeting-activity-update', () => {
        batchUpdate(() => {
          const items: MeetingLogEntry[] = [];
          snap.forEach((d) => items.push({ id: d.id, ...(d.data() as any) }));
          setLogs(items);
          setLoading(false);
        });
      });
    }, 100),
    []
  );

  useEffect(() => {
    if (!meetingId || !meetingQuery) {
      setLogs([]);
      setLoading(false);
      return;
    }

    // Clean up previous listener
    if (unsubRef.current) {
      unsubRef.current();
    }

    const unsub = onSnapshot(meetingQuery, debouncedUpdate, (error) => {
      console.error('Meeting activity listener error:', error);
      setLoading(false);
    });

    unsubRef.current = unsub;

    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [meetingId, meetingQuery, debouncedUpdate]);

  return { logs, loading };
};

export const formatLogTimestampGhana = (ts: any) => {
  try {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-US', { 
      timeZone: 'Africa/Accra', 
      hour: '2-digit', 
      minute: '2-digit', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  } catch {
    return '';
  }
};


